/* Moomo R2 · PDP behaviour: pack × add-on → one variant id, quantity stepper, mobile gallery counter,
   mobile sticky add-to-cart bar, desktop sticky-panel short-viewport fallback. */
(() => {
  const formatMoney = (cents, format) => {
    const value = (cents / 100).toFixed(2);
    const [whole, dec] = value.split('.');
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${whole}.${dec}`.replace('.', ','))
      .replace(/\{\{\s*amount\s*\}\}/, `${withCommas}.${dec}`);
  };

  const init = (root) => {
    if (root.dataset.mmReady) return;
    root.dataset.mmReady = '1';
    const variants = JSON.parse(root.querySelector('[data-mm-variants]').textContent);
    const cfg = JSON.parse(root.querySelector('[data-mm-config]').textContent);
    const idInput = root.querySelector('[data-mm-variant-id]');
    const instInput = root.querySelector('[data-mm-installment-id]');
    const addBtn = root.querySelector('.mm-pdp__add');
    const addLabel = root.querySelector('[data-mm-add-label]');
    const priceEl = root.querySelector('[data-mm-price]');
    const barPrice = root.querySelector('[data-mm-bar-price]');
    const deltaEl = root.querySelector('[data-mm-addon-delta]');
    const addon = root.querySelector('input[name="mm-addon"]');
    const select = root.querySelector('[data-mm-select]');
    const money = (c) => formatMoney(c, cfg.money);

    const packValue = () => {
      const checked = root.querySelector('input[name="mm-pack"]:checked') || root.querySelector('input[name="mm-pack"][type="hidden"]');
      return checked ? checked.value : null;
    };
    const find = (title) => variants.find((v) => v.title === title);

    const apply = (variant) => {
      if (!variant) return;
      idInput.value = variant.id;
      if (instInput) { instInput.value = variant.id; instInput.dispatchEvent(new Event('change', { bubbles: true })); }
      priceEl.textContent = variant.priceText;
      if (barPrice) barPrice.textContent = variant.priceText;
      addBtn.disabled = !variant.available;
      addBtn.setAttribute('aria-disabled', String(!variant.available));
      addLabel.innerHTML = variant.available
        ? `${cfg.addLabel}<span class="mm-desktop-only"> · ${variant.priceText}</span>`
        : cfg.soldOut;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    };

    const resolve = () => {
      if (select) return apply(variants.find((v) => String(v.id) === select.value));
      const pack = packValue();
      if (!pack) return;
      const base = find(pack);
      const withAddon = find(pack + cfg.suffix);
      if (deltaEl && base && withAddon) {
        deltaEl.textContent = money(withAddon.price - base.price).replace(/\.00(?=\D*$)/, '');
        addon.disabled = !withAddon.available;
      }
      apply(addon && addon.checked && withAddon ? withAddon : base);
    };

    root.querySelectorAll('input[name="mm-pack"]').forEach((r) => r.addEventListener('change', resolve));
    if (addon) addon.addEventListener('change', resolve);
    if (select) select.addEventListener('change', resolve);
    resolve();

    /* quantity */
    const qty = root.querySelector('.mm-qty__input');
    const minus = root.querySelector('[data-mm-qty="-1"]');
    const syncQty = () => { minus.disabled = (parseInt(qty.value, 10) || 1) <= 1; };
    root.querySelectorAll('[data-mm-qty]').forEach((b) => b.addEventListener('click', () => {
      qty.value = Math.max(1, (parseInt(qty.value, 10) || 1) + parseInt(b.dataset.mmQty, 10));
      syncQty();
    }));
    qty.addEventListener('change', () => { if ((parseInt(qty.value, 10) || 0) < 1) qty.value = 1; syncQty(); });
    syncQty();

    /* mobile gallery counter */
    const track = root.querySelector('.mm-pdp__track');
    const cur = root.querySelector('[data-mm-current]');
    const dots = [...root.querySelectorAll('.mm-pdp__dot')];
    if (track && cur) {
      let raf;
      track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const slide = track.querySelector('.mm-pdp__slide');
          if (!slide) return;
          const i = Math.round(track.scrollLeft / (slide.getBoundingClientRect().width + 12));
          cur.textContent = String(i + 1);
          dots.forEach((d, n) => d.classList.toggle('is-active', n === i));
        });
      }, { passive: true });
    }

    /* mobile sticky add-to-cart bar */
    const bar = root.querySelector('[data-mm-bar]');
    const buy = root.querySelector('[data-mm-buy]');
    const footer = document.querySelector('.mm-footer');
    const mobile = window.matchMedia('(max-width: 989px)');
    if (bar && buy && 'IntersectionObserver' in window) {
      let buyVisible = true; let footerVisible = false;
      const update = () => {
        const show = mobile.matches && !buyVisible && !footerVisible;
        bar.hidden = !show;
        document.body.classList.toggle('mm-has-atc-bar', show);
      };
      new IntersectionObserver(([e]) => { buyVisible = e.isIntersecting || e.boundingClientRect.top > 0; update(); }).observe(buy);
      if (footer) new IntersectionObserver(([e]) => { footerVisible = e.isIntersecting; update(); }).observe(footer);
      mobile.addEventListener('change', update);
      root.querySelector('[data-mm-bar-add]').addEventListener('click', () => {
        if (root.querySelector('input[name="mm-pack"]') && !packValue()) {
          const packs = root.querySelector('.mm-pdp__packs');
          packs.scrollIntoView({ behavior: 'smooth', block: 'center' });
          packs.querySelector('input').focus({ preventScroll: true });
          return;
        }
        addBtn.click();
      });
    }

    /* desktop sticky panel: a panel taller than the viewport (it now carries the full write-up) scrolls with the page
       until its bottom is in view, then sticks — so every line stays reachable */
    const panel = root.querySelector('[data-mm-panel]');
    const checkSticky = () => {
      if (!panel) return;
      const headerH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--mm-header-h')) || 72;
      const top = Math.min(headerH + 24, window.innerHeight - panel.offsetHeight - 24);
      panel.style.top = top + 'px';
    };
    checkSticky();
    window.addEventListener('resize', checkSticky);
    if (panel && 'ResizeObserver' in window) new ResizeObserver(checkSticky).observe(panel);
    root.addEventListener('change', () => requestAnimationFrame(checkSticky));
  };

  const boot = () => document.querySelectorAll('[data-mm-pdp]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
